import {
  Autocomplete,
  type AutocompleteChangeReason,
  Box,
  TextField,
} from "@mui/material";
import { useHunterSearchQuery } from "Api/Queries/User/useHunterSearchQuery";
import {
  getHunterOptionLabel,
  isHunterLicenseInactive,
  isHunterOptionEqualToValue,
  renderHunterOption,
  sortHuntersByLicenseActivity,
} from "Components/Shared/Inputs/hunterAutocompleteHelpers";
import { useDebouncedSearch } from "Hooks/useDebouncedSearch";
import type { HunterSearchDto, Region } from "Infrastructure/Api/Api";
import { useMemo, useState } from "react";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.Shared.HunterAutocomplete;

type Props = {
  selectedHunters: Array<HunterSearchDto>;
  onHuntersChange: (hunters: Array<HunterSearchDto>) => void;
  disabled?: boolean;
  label?: string;
  /** Only offer hunters with a license of this market — commission is shared within one market. */
  region?: Region | null;
};

export const HunterAutocomplete = ({
  selectedHunters,
  onHuntersChange,
  disabled = false,
  label,
  region,
}: Props) => {
  const [inputValue, setInputValue] = useState("");
  const { searchTerm, setSearchTerm, debouncedSearchTerm } =
    useDebouncedSearch();
  const { t } = useResource();
  const resolvedLabel = label ?? t(COMPONENT_RESOURCES.SearchHunter);

  const { data: searchResults, isLoading } = useHunterSearchQuery(
    debouncedSearchTerm,
    debouncedSearchTerm.length >= 2,
    region,
  );

  const sortedResults = useMemo(
    () => sortHuntersByLicenseActivity(searchResults ?? []),
    [searchResults],
  );

  const handleInputChange = (_e: unknown, newInputValue: string) => {
    setInputValue(newInputValue);
    setSearchTerm(newInputValue);
  };

  const handleChange = (
    _e: unknown,
    newValue: Array<HunterSearchDto>,
    reason: AutocompleteChangeReason,
  ) => {
    // Prevent removal of items when input is empty (backspace behavior)
    if (newValue.length < selectedHunters.length && inputValue === "") {
      return;
    }
    // The removal should happen only through or delete icon in the hunter list, otherwise the state wont update correctly disabling the native autocomplete removal
    if (reason === "removeOption") {
      return;
    }
    onHuntersChange(newValue);
  };

  return (
    <Box>
      {/** biome-ignore lint/a11y/noLabelWithoutControl: associated by wrapping the input */}
      <label>
        {resolvedLabel}
        <Autocomplete
          multiple
          disableClearable
          options={sortedResults}
          getOptionLabel={getHunterOptionLabel}
          value={selectedHunters}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          loading={isLoading}
          disabled={disabled}
          filterOptions={(x) => x} // Disable built-in filtering since we're using server-side search
          getOptionDisabled={isHunterLicenseInactive}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              placeholder={t(COMPONENT_RESOURCES.SearchHunterPlaceholder)}
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                  "& input": {
                    height: "12px",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                  borderRadius: 1,
                },
                "& .MuiAutocomplete-endAdornment": {
                  display: "none",
                },
              }}
            />
          )}
          renderOption={renderHunterOption}
          renderTags={() => null} // no tags
          isOptionEqualToValue={isHunterOptionEqualToValue}
          noOptionsText={
            searchTerm.length < 2
              ? t(COMPONENT_RESOURCES.SearchHunterMinLength)
              : t(COMPONENT_RESOURCES.SearchHunterNoResults)
          }
        />
      </label>
    </Box>
  );
};
